import classNames from 'classnames/bind';
import { HTMLAttributes } from 'react';
import { CustomFieldProps } from './types';
import PhoneLink from '@components/PhoneLink';
import dayjs, { Dayjs } from 'dayjs';

import styles from './Form.module.css';

const classNamesCtx = classNames.bind(styles);

type AppReadonlyInputProps = HTMLAttributes<HTMLDivElement> & {
	customProps: {
		/** String of the input label */
		labelText?: string | null;
		labelValue?: string | Dayjs | null;
		dayjsFormat?: string;
		asPhoneLink?: boolean;
	};
};

/**
 * Wrapper around MUI TextField to provide application-wide default behaviors.
 */
export default function ReadonlyInput({ customProps, ...props }: AppReadonlyInputProps) {
	const classes = classNamesCtx({
		['readonly-input']: true,
		[props.className ?? '']: Boolean(props.className)
	});

	if (customProps.labelValue instanceof dayjs || typeof customProps.labelValue === 'object') {
		return (
			<div className={classes}>
				<span className={styles.label}>{customProps.labelText}</span>

				<span
					className={styles['value']}
					title={customProps.labelValue?.format(customProps.dayjsFormat)}
				>
					{customProps.labelValue?.format(customProps.dayjsFormat) ?? '-'}
				</span>
			</div>
		);
	}

	return (
		<div className={classes}>
			<span className={styles.label}>{customProps.labelText}</span>
			{customProps.asPhoneLink ? (
				<PhoneLink
					phoneNumber={customProps.labelValue ?? ''}
					emptyText='-'
					className={styles.bold}
				/>
			) : (
				<span
					className={styles['value']}
					title={customProps.labelValue ?? ''}
				>
					{customProps.labelValue ?? '-'}
				</span>
			)}
		</div>
	);
}
function asReadonlyInput() {
	return ({ field, form, ...props }: CustomFieldProps) => {
		const { value } = form.getFieldMeta<string | Dayjs | null>(field.name);

		return (
			<ReadonlyInput
				customProps={{
					labelValue: value,
					labelText: props.label,
					asPhoneLink: props.asPhoneLink,
					dayjsFormat: props.dayjsFormat
				}}
				className={props.className}
			/>
		);
	};
}

export const FormikReadonlyInput = asReadonlyInput();
